/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2019, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import React, { Component } from 'react';
import PropTypes from 'util/PropTypes';
import { observer } from 'mobx-react';

import FieldFlyout from './FieldFlyout';
import { Tags, TruncatedText } from '@ba/ba-ui-carbon-toolkit';

import './fieldsList.scss';

const FIELD_LABEL_MAX_WIDTH = 198; // adjusted from R7 setting (200px) to get the same truncated texts in 11.2 for interesting fields 

@observer
class FieldsList extends Component {

	constructor(props) {
		super(props);
		this.fieldsListRef = React.createRef();

		this.state = {
			tagUnderHover: null,
			tagNodeUnderHover: null
		};
	}

	_onButtonClick(index) {
		this.props.onSelectField(this.props.fields[index]);
	}

	_onDeleteField(value) {
		const field = this.props.fields
			.find(field => field.id === value);
		if (field) {
			this.props.onDeleteField(field);
		}
	}

	render() {
		const { error, className } = this.props;
		if(error) {
			return this._renderError();
		}

		return (
			<div className={className + '--container'}>
				{
					this.props.title && (
						<div className={className + '--title'}>
							{this.props.title}
						</div>
					)
				}
				<div className={className + '--tags'}
					onKeyDown={(e) => this._onKeyDown(e)}
					ref={this.fieldsListRef}
					tabIndex="0"
				>
					{this._renderTags()}
					{this._renderHoveredTagFlyout()}
				</div>
			</div>
		);
	}

	_renderTags = () => {
		const { fields, className } = this.props;
		const tags = fields.map((field, index) => {
			const { id, label } = field;
			return {
				value: id,
				label: <TruncatedText value={label} allowTooltip={false}/>,
				color: 'gray',
				onClick: this._onButtonClick.bind(this, index)
			};
		});
		const hasDeleteable = fields.find(field => !!field.deletable);
		const tagDeleteIcon = hasDeleteable ? {
			onClick: (e, currentTag) => {
				if (e && e.target && currentTag && currentTag.value) {
					this._onDeleteField(currentTag.value);
				}
			}
		} : null;
		return (
			<Tags
				className={className}
				tags={tags}
				fullWidth={true}
				removable={!!hasDeleteable}
				tagActionIcon={tagDeleteIcon}
				onTagMouseEnter={this._onTagMouseEnter}
				onTagMouseLeave={this._clearFlyoutState}
				style={ { 'max-width': `${FIELD_LABEL_MAX_WIDTH}px` } }
			/>
		);
	}

	_onTagMouseEnter = (tag, event) => {
		if (event && event.currentTarget) {
			this.setState({
				tagUnderHover: tag,
				tagNodeUnderHover: event.currentTarget
			});
		}
	}

	_clearFlyoutState = () => {
		this.setState({
			tagUnderHover: null,
			tagNodeUnderHover: null
		});
	}

	_renderHoveredTagFlyout = () => {
		if (this.state.tagUnderHover && this.state.tagNodeUnderHover) {
			return (
				<FieldFlyout
					triggerNode={this.state.tagNodeUnderHover}
					onClose={this._clearFlyoutState}
					activeDataSourceModule={this.props.activeDataSourceModule}
					id={this.state.tagUnderHover.value}
				/>
			);
		}
	}

	_renderError(error = this.props.error) {
		const { className } = this.props;
		return (
			<div className={className + '--container'}>
				<div className='fieldsListTitle'>{error.title}</div>
				<div className='fieldsListErrorMessage'>{error.description}</div>
			</div>
		);
	}


	_getFieldListNode() {
		return this.fieldsListRef.current;
	}
	_onKeyDown(event) {
		const fieldsListNode = this._getFieldListNode();
		const listItems = Array.from(fieldsListNode.querySelectorAll('.ba-common-tags__listItem'));
		const index = listItems.indexOf(event.target);
		if (index !== -1) {
			switch (event.key) {
				case 'ArrowLeft':
					if (index > 0) {
						this._setButtonTabIndex(fieldsListNode, index - 1, '0');
						this._focusFieldButtonNode(fieldsListNode, index - 1);
						this._blurFieldButtonNode(fieldsListNode, index);
					}
					event.preventDefault();
					break;
				case 'ArrowRight':
					if (index < this.props.fields.length - 1) {
						this._setButtonTabIndex(fieldsListNode, index + 1, '0');
						this._focusFieldButtonNode(fieldsListNode, index + 1);
						this._blurFieldButtonNode(fieldsListNode, index);
					}
					event.preventDefault();
					break;
				case 'Tab':
					for(let i = 0; i <= this.props.fields.length - 1; i++) {
						if (i !== index) {
							this._setButtonTabIndex(fieldsListNode, i, '-1');
						}
					}
					this._blurFieldButtonNode(fieldsListNode, index);
					break;
				case 'Enter':
					this._onButtonClick(index);
					break;
				default:
					break;
			}
		}
	}
	_setButtonTabIndex(fieldsListNode, index, tabIndex) {
		fieldsListNode.querySelector('.ba-common-tags__list').children[index].tabIndex = tabIndex;
	}
	_getButtonNode(listNode, index) {
		return listNode.querySelector('.ba-common-tags__list').children[index];
	}
	_blurFieldButtonNode(listNode, index) {
		const node = this._getButtonNode(listNode, index);
		node.blur();
		node.classList.remove('is-active');
		node.classList.remove('is-focus');
	}
	_focusFieldButtonNode(listNode, index) {
		const node = this._getButtonNode(listNode, index);
		node.focus();
		node.classList.add('is-focus');

	}
}

FieldsList.propTypes = {
	className: PropTypes.string,
	fields: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.string,
		label: PropTypes.string,
		deletable: PropTypes.bool
	})),
	error: PropTypes.shape({
		title: PropTypes.string,
		description: PropTypes.string
	}),
	onSelectField: PropTypes.func,
	onDeleteField: PropTypes.func,
	title: PropTypes.string,
	activeDataSourceModule: PropTypes.observableObject,
	shouldUseDisambiguatedPaths: PropTypes.bool,
	relationshipVM: PropTypes.observableObject.isRequired
};

FieldsList.defaultProps = {
	className: 'fieldsList',
	fields: [],
	onSelectField: () => {},
	onDeleteField: () => {}
};

export default FieldsList;



// WEBPACK FOOTER //
// ./src/js/components/fieldsList/FieldsList.js